import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { BreadcrumbSchema } from "@/components/StructuredData";
import { Button } from "@/components/ui/button";
import { 
  CheckCircle, 
  ArrowRight, 
  Clock, 
  PoundSterling, 
  Award, 
  Layers, 
  MapPin, 
  MessageSquare 
} from "lucide-react";

const DEV_BREADCRUMBS = [
  { name: "Home", url: "/" },
  { name: "For Developers", url: "/for-developers" }
];

const ForDevelopersPage = () => {
  const services = [
    {
      id: "dev-service-extensions",
      title: "Extensions & Conversions",
      description: "Single and double storey extensions, loft and garage conversions — adding value to your assets."
    },
    {
      id: "dev-service-refurbishments",
      title: "Full Refurbishments",
      description: "End-to-end property refurbishment and renovation — strip-out to final snag."
    },
    {
      id: "dev-service-kitchen-bathroom",
      title: "Kitchen & Bathroom Fit-Out",
      description: "Complete installations — finished to a standard buyers and tenants expect."
    },
    {
      id: "dev-service-carpentry",
      title: "Carpentry & Joinery",
      description: "First and second fix carpentry, bespoke joinery, all finish carpentry work."
    },
    {
      id: "dev-service-general",
      title: "General Building",
      description: "Groundworks, brickwork, plastering, decorating — handling full scope under one contractor."
    },
    {
      id: "dev-service-flexibility",
      title: "Project Flexibility",
      description: "Flex around development pipelines, multiple projects, phased programmes — we make it work."
    }
  ];

  const reasons = [
    {
      id: "dev-reason-programme",
      icon: Clock,
      title: "We keep to programme",
      description: "Delays cost money. We plan properly, communicate changes early, and deliver as promised."
    },
    {
      id: "dev-reason-pricing",
      icon: PoundSterling,
      title: "Transparent, competitive pricing",
      description: "Detailed cost breakdowns from the outset, no hidden extras — easier cost control, no late surprises."
    },
    {
      id: "dev-reason-quality",
      icon: Award,
      title: "Quality that holds up",
      description: "Snagging costs time and money. We focus on doing it right first time — a high-quality finish that reflects well on your development."
    },
    {
      id: "dev-reason-scope",
      icon: Layers,
      title: "Full scope under one contractor",
      description: "From groundworks to decoration — fewer contractors to manage, fewer coordination headaches."
    },
    {
      id: "dev-reason-local",
      icon: MapPin,
      title: "Local to Surrey and SW London",
      description: "We know the area, the planning environment, and we're quickly on hand when you need us."
    },
    {
      id: "dev-reason-communicate",
      icon: MessageSquare,
      title: "A contractor who communicates",
      description: "No chasing or silence — clear, straightforward updates throughout your project."
    }
  ];

  const coverageAreas = [
    "Epsom", "Sutton", "Cheam", "Banstead", 
    "Carshalton", "Wallington", "Croydon", "Kingston", "Wimbledon"
  ];

  return (
    <div className="min-h-screen bg-stone-50">
      <SEO 
        title="Contractor for Developers | TJP Construction Surrey"
        description="Reliable building contractor for property developers in Surrey & SW London. TJP Construction delivers quality residential builds on time and on budget."
        keywords="developer contractor surrey, residential development sw london, property developer builder, construction contractor"
        url="/for-developers"
      />
      <BreadcrumbSchema items={DEV_BREADCRUMBS} />
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 to-slate-800 py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl">
            <p className="text-amber-500 font-medium tracking-wide uppercase text-sm mb-4">
              For Developers
            </p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-white mb-6 leading-tight">
              Quality Build. On Time. On Budget.
            </h1>
            <p className="text-xl text-stone-300 mb-8 leading-relaxed">
              A dependable local contractor for residential developers in Surrey and SW London — 
              covering the full scope, keeping to programme, and delivering the finish your buyers and tenants expect.
            </p>
            <Link to="/contact">
              <Button 
                className="bg-amber-600 text-white hover:bg-amber-500 h-14 px-10 font-bold text-base shadow-lg"
                data-testid="developer-hero-cta"
              >
                Discuss Your Next Project
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-6">
              A Contractor Who Delivers
            </h2>
            <p className="text-lg text-stone-600 leading-relaxed mb-6">
              Developer margins depend on punctual, quality-delivering contractors. 
              Delays, hidden costs, and poor finishes eat into your profit and reputation.
            </p>
            <p className="text-lg text-stone-600 leading-relaxed">
              TJP is a qualified carpentry and construction firm based in <strong>Ewell, Surrey</strong>. 
              We deliver solid work, keep to programme, offer straightforward costs with no hidden extras, 
              and cover the full scope — reducing your contractor management overhead.
            </p>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm font-medium tracking-wide uppercase text-amber-700 mb-4">
              What We Deliver
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-4">
              Full Scope Construction
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              From initial groundworks to final snagging — we cover everything.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <div 
                key={service.id}
                className="bg-stone-50 rounded-lg p-8 border border-stone-100 hover:border-amber-200 transition-colors"
              >
                <h3 className="text-xl font-bold text-slate-900 mb-3">{service.title}</h3>
                <p className="text-stone-600 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Developers Work With TJP */}
      <section className="py-20 md:py-28 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm font-medium tracking-wide uppercase text-amber-500 mb-4">
              Why Work With Us
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-4">
              Why Developers Work With TJP
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {reasons.map((reason) => (
              <div 
                key={reason.id}
                className="bg-slate-800/50 rounded-lg p-8 border border-slate-700"
              >
                <div className="w-12 h-12 bg-amber-600/20 rounded-lg flex items-center justify-center mb-6">
                  <reason.icon className="text-amber-500" size={24} />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{reason.title}</h3>
                <p className="text-stone-400 leading-relaxed">{reason.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage Area */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm font-medium tracking-wide uppercase text-amber-700 mb-4">
                Where We Work
              </p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-6">
                Coverage Area
              </h2>
              <p className="text-lg text-stone-600 leading-relaxed mb-4">
                Based in <strong>Ewell, Surrey</strong>, we serve the <strong>SM, CR, and SW postcode areas</strong>.
              </p>
              <div className="flex flex-wrap gap-3 mb-4">
                {coverageAreas.map((area) => (
                  <span 
                    key={`dev-area-${area.toLowerCase().replace(/\s+/g, '-')}`}
                    className="bg-stone-100 text-stone-700 px-4 py-2 rounded-full text-sm font-medium"
                  >
                    {area}
                  </span>
                ))}
              </div>
              <p className="text-stone-500 text-sm">
                And surrounding areas — local, responsive, and available.
              </p>
            </div>
            <div className="bg-slate-900 rounded-lg p-8 text-center">
              <MapPin className="text-amber-500 mx-auto mb-4" size={48} />
              <h3 className="text-2xl font-bold text-white mb-2">Based in Ewell, Surrey</h3>
              <p className="text-stone-400">
                Local knowledge, quick response times, and on-hand support when you need it
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-amber-700">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Let's Talk
          </h2>
          <p className="text-xl text-amber-100 mb-8 leading-relaxed">
            Got an upcoming project or a pipeline you need a reliable contractor for? 
            We're happy to discuss your requirements, visit site, and provide a detailed quote.
          </p>
          <Link to="/contact">
            <Button 
              className="bg-white text-amber-700 hover:bg-amber-50 h-14 px-10 font-bold text-base shadow-lg"
              data-testid="developer-footer-cta"
            >
              Get in Touch with Tony
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ForDevelopersPage;
